"use client";

import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

interface LoginMethodCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  isLastUsed?: boolean;
  isLoading?: boolean;
  isExpanded?: boolean;
  onClick: () => void;
  children?: React.ReactNode;
  disabled?: boolean;
}

export function LoginMethodCard({
  icon,
  title,
  subtitle,
  isLastUsed,
  isLoading,
  isExpanded,
  onClick,
  children,
  disabled,
}: LoginMethodCardProps) {
  const hasExpandable = !!children;

  return (
    <motion.div
      layout
      className={cn(
        "overflow-hidden rounded-lg border transition-colors duration-150",
        isLastUsed
          ? "border-primary/30 bg-primary/[0.06]"
          : "border-border/10 bg-card dark:border-white/10 dark:bg-card",
      )}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || isLoading}
        className={cn(
          "flex w-full items-center gap-3.5 px-4 py-3.5 text-left",
          "transition-colors duration-150",
          "hover:bg-black/[0.035] active:bg-black/[0.055] dark:hover:bg-white/[0.065] dark:active:bg-white/[0.08]",
          "disabled:cursor-not-allowed disabled:opacity-50",
          isExpanded && hasExpandable
            ? "rounded-b-none rounded-t-lg"
            : "rounded-lg",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/70",
        )}
      >
        {/* Icon slot */}
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border/[0.07] bg-black/[0.035] text-foreground/75 dark:border-white/[0.06] dark:bg-white/[0.055] dark:text-foreground/75">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            icon
          )}
        </span>

        {/* Text */}
        <span className="min-w-0 flex-1">
          <span className="block font-small text-[0.8125rem] font-medium leading-snug text-foreground">
            {title}
          </span>
          <span className="mt-0.5 block font-small text-xs leading-snug text-muted-foreground">
            {subtitle}
          </span>
        </span>

        {/* Last used badge */}
        {isLastUsed && (
          <span className="bg-success/12 shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tracking-wide text-success dark:text-success">
            Last used
          </span>
        )}
      </button>

      {/* Expandable section */}
      <AnimatePresence initial={false}>
        {isExpanded && children && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-border/[0.07] px-4 pb-4 pt-3 dark:border-white/[0.07]">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
